/**
 * Event Registration Modal Component
 * Shows event details first, then registration form
 */

import React, { useState, useEffect } from 'react';
import { X, Calendar, CheckCircle, Loader2, Clock, User, ArrowRight, Linkedin, Download } from 'lucide-react';
import { registerForEvent, EventRegistrationRequest, getEventDetails, getCalendarLinks, EventDetails, CalendarLinks } from '@/services/api/eventRegistrations';
import PhoneInput from '@/components/ui/PhoneInput';

interface EventRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: {
    id: string;
    title: string;
    date: string;
    time: string;
  };
}

type ModalView = 'details' | 'form' | 'success';

export default function EventRegistrationModal({
  isOpen,
  onClose,
  event
}: EventRegistrationModalProps) {
  const [view, setView] = useState<ModalView>('details');
  const [eventDetails, setEventDetails] = useState<EventDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    organization_name: '',
    phone_number: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoomLink, setZoomLink] = useState<string | null>(null);
  const [calendarLinks, setCalendarLinks] = useState<CalendarLinks | null>(null);

  // Fetch event details when modal opens
  useEffect(() => {
    if (isOpen && event.id) {
      fetchEventDetails();
    }
  }, [isOpen, event.id]);

  const fetchEventDetails = async () => {
    setLoadingDetails(true);
    try {
      const details = await getEventDetails(event.id);
      setEventDetails(details);
    } catch (err) {
      console.error('Failed to fetch event details:', err);
      // Continue without details - user can still register
    } finally {
      setLoadingDetails(false);
    }
  };

  // Generate calendar links on the frontend as fallback
  const generateFrontendCalendarLinks = (): CalendarLinks => {
    // Parse date and time
    const eventDateTime = new Date(`${event.date} ${event.time}`);
    const duration = eventDetails?.duration_minutes || 60;
    const endDateTime = new Date(eventDateTime.getTime() + duration * 60000);

    // Format for Google Calendar (YYYYMMDDTHHmmss)
    const formatGoogleDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    };

    const startStr = formatGoogleDate(eventDateTime);
    const endStr = formatGoogleDate(endDateTime);

    const description = zoomLink
      ? `Join the webinar: ${zoomLink}\n\n${eventDetails?.description || ''}`
      : eventDetails?.description || '';

    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${startStr}/${endStr}&details=${encodeURIComponent(description)}`;

    const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(event.title)}&startdt=${eventDateTime.toISOString()}&enddt=${endDateTime.toISOString()}&body=${encodeURIComponent(description)}`;

    // ICS content
    const icsDescription = description.replace(/\n/g, '\\n');
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//WeaveIn//Events//EN
BEGIN:VEVENT
DTSTART:${startStr}
DTEND:${endStr}
SUMMARY:${event.title}
DESCRIPTION:${icsDescription}
URL:${zoomLink || ''}
END:VEVENT
END:VCALENDAR`;

    return {
      google: googleUrl,
      outlook: outlookUrl,
      ics_content: icsContent,
      start_datetime: eventDateTime.toISOString(),
      end_datetime: endDateTime.toISOString()
    };
  };

  const fetchCalendarLinks = async () => {
    try {
      const response = await getCalendarLinks(event.id);
      setCalendarLinks(response.calendar_links);
    } catch (err) {
      console.error('Failed to fetch calendar links from API, using frontend fallback:', err);
      // Generate links on frontend as fallback
      setCalendarLinks(generateFrontendCalendarLinks());
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const registrationData: EventRegistrationRequest = {
        ...formData,
        organization_name: formData.organization_name.trim() || undefined,
        event_id: event.id,
        event_title: event.title,
        event_date: `${event.date} at ${event.time}`
      };

      const response = await registerForEvent(registrationData);

      if (response.success) {
        setView('success');
        setZoomLink(response.zoom_link || null);
        // Fetch calendar links after successful registration
        fetchCalendarLinks();
      } else {
        setError(response.message || 'Registration failed. Please try again.');
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset state on close
    setFormData({
      full_name: '',
      email: '',
      organization_name: '',
      phone_number: ''
    });
    setView('details');
    setError(null);
    setZoomLink(null);
    setCalendarLinks(null);
    setEventDetails(null);
    onClose();
  };

  const downloadICS = () => {
    if (!calendarLinks?.ics_content) return;

    const blob = new Blob([calendarLinks.ics_content], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${event.title.replace(/[^a-zA-Z0-9]/g, '_')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  const description = eventDetails?.description || '';
  const speakers = eventDetails?.speakers || [];
  const duration = eventDetails?.duration_minutes;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-paper border-b border-v2border p-6 text-ink relative flex-shrink-0">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-ink-500 hover:text-ink transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="w-6 h-6" />
            <span className="text-sm font-medium opacity-90">
              {view === 'success' ? 'Registration Complete' : 'Upcoming Webinar'}
            </span>
          </div>
          <h2 className="text-xl font-bold pr-8">{event.title}</h2>
          <div className="flex items-center gap-4 mt-2 text-sm opacity-90">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {event.date}
            </span>
            {event.time && (
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {event.time}
              </span>
            )}
            {duration && (
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {duration} min
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {view === 'success' ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                You're Registered!
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Check your email for confirmation and the webinar link.
              </p>

              {/* Calendar Links */}
              <div className="space-y-3 mb-6">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Add to your calendar:
                </p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  {calendarLinks?.google && (
                    <a
                      href={calendarLinks.google}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 22q-2.05 0-3.875-.788-1.825-.787-3.187-2.15-1.363-1.362-2.15-3.187Q2 14.05 2 12q0-2.075.788-3.887.787-1.813 2.15-3.175Q6.3 3.575 8.125 2.787 9.95 2 12 2q2.075 0 3.887.787 1.813.788 3.175 2.151 1.363 1.362 2.15 3.175Q22 9.925 22 12v1.45q0 1.475-1.012 2.512Q19.975 17 18.5 17q-.875 0-1.65-.375t-1.3-1.075q-.725.725-1.638 1.088Q12.999 17 12 17q-2.075 0-3.537-1.463Q7 14.075 7 12t1.463-3.538Q9.925 7 12 7t3.538 1.462Q17 9.925 17 12v1.45q0 .65.425 1.1.425.45 1.075.45.65 0 1.075-.45.425-.45.425-1.1V12q0-3.35-2.325-5.675Q15.35 4 12 4 8.65 4 6.325 6.325 4 8.65 4 12t2.325 5.675Q8.65 20 12 20h5v2Zm0-7q1.25 0 2.125-.875T15 12q0-1.25-.875-2.125T12 9q-1.25 0-2.125.875T9 12q0 1.25.875 2.125T12 15Z"/>
                      </svg>
                      Google Calendar
                    </a>
                  )}
                  {calendarLinks?.outlook && (
                    <a
                      href={calendarLinks.outlook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V5.3l1.4 1.4q.17.15.17.45V12zm-8.62 0q0-.88-.33-1.58-.31-.72-.88-1.23-.55-.49-1.28-.79-.75-.28-1.58-.28-.83 0-1.57.29-.73.29-1.29.79-.55.51-.88 1.22-.33.69-.33 1.58 0 .84.33 1.55.33.71.88 1.22.55.51 1.28.81.73.28 1.57.28.84 0 1.59-.28.73-.3 1.28-.81.55-.5.88-1.22.33-.72.33-1.55zm1.47-7.47V4.2h-8.6v4.8h3.13q.1-.67.5-1.14.39-.45.94-.72.56-.28 1.24-.28.78 0 1.41.35.66.34 1.1.92.49.59.54 1.38zm1.59.13L16.96 6.2h-4.9v.01l7.18.01v-.56zm-.11 10.42V13.8H14.5v6.35h3.6q.2 0 .37-.16.16-.17.16-.37zM6.25 14.56h-.52v.9h.52q.24 0 .36-.12.14-.12.14-.34 0-.2-.14-.32-.12-.12-.36-.12zm.1-2.33h-.62v.95h.62q.25 0 .39-.13.13-.12.13-.34 0-.21-.13-.35-.13-.13-.39-.13zm.44-1.67H5.12v5.19h.67v-1.93h.56q.53 0 .86-.3.31-.3.31-.73 0-.47-.31-.77-.32-.31-.83-.31l-.09.01z"/>
                      </svg>
                      Outlook
                    </a>
                  )}
                  {calendarLinks?.ics_content && (
                    <button
                      onClick={downloadICS}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
                    >
                      <Download className="w-4 h-4" />
                      Download .ics
                    </button>
                  )}
                </div>
              </div>

              {/* Zoom Link */}
              {zoomLink && (
                <div className="mb-4">
                  <a
                    href={zoomLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-ink text-paper px-6 py-3 rounded-lg font-semibold hover:bg-ink-700 transition-colors"
                  >
                    Join Webinar
                  </a>
                </div>
              )}

              <button
                onClick={handleClose}
                className="block w-full text-gray-600 dark:text-gray-400 hover:text-ink font-medium"
              >
                Close
              </button>
            </div>
          ) : view === 'details' ? (
            <div className="space-y-6">
              {loadingDetails ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-ink" />
                </div>
              ) : (
                <>
                  {/* Description */}
                  {description && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                        About This Event
                      </h3>
                      <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                        {description}
                      </p>
                    </div>
                  )}

                  {/* Speakers */}
                  {speakers.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                        {speakers.length === 1 ? 'Speaker' : 'Speakers'}
                      </h3>
                      <div className="space-y-3">
                        {speakers.map((speaker, index) => (
                          <div key={index} className="flex items-start gap-3">
                            {speaker.photo_url ? (
                              <img
                                src={speaker.photo_url}
                                alt={speaker.name}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-v2accent-soft flex items-center justify-center">
                                <User className="w-6 h-6 text-ink" />
                              </div>
                            )}
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-gray-900 dark:text-white">
                                  {speaker.name}
                                </p>
                                {speaker.linkedin_url && (
                                  <a
                                    href={speaker.linkedin_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#0077B5] hover:opacity-80"
                                  >
                                    <Linkedin className="w-4 h-4" />
                                  </a>
                                )}
                              </div>
                              {(speaker.title || speaker.organization) && (
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  {[speaker.title, speaker.organization].filter(Boolean).join(' • ')}
                                </p>
                              )}
                              {speaker.bio && (
                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 line-clamp-2">
                                  {speaker.bio}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* No details fallback */}
                  {!description && speakers.length === 0 && !loadingDetails && (
                    <p className="text-gray-600 dark:text-gray-400 text-center py-4">
                      Join us for this upcoming event. Register now to secure your spot!
                    </p>
                  )}
                </>
              )}

              {/* Register Button */}
              <button
                onClick={() => setView('form')}
                className="w-full bg-ink text-paper py-3 rounded-lg font-semibold hover:bg-ink-700 transition-colors flex items-center justify-center gap-2"
              >
                Register Now
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-v2accent focus:border-transparent"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-v2accent focus:border-transparent"
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Organization Name
                </label>
                <input
                  type="text"
                  value={formData.organization_name}
                  onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-v2accent focus:border-transparent"
                  placeholder="Acme Corp (optional - leave blank for individual)"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Leave blank if registering as an individual
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <PhoneInput
                  required
                  value={formData.phone_number}
                  onChange={(v) => setFormData({ ...formData, phone_number: v })}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-v2accent focus:border-transparent"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setView('details')}
                  className="flex-1 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 py-3 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-ink text-paper py-3 rounded-lg font-semibold hover:bg-ink-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Registering...
                    </>
                  ) : (
                    'Complete Registration'
                  )}
                </button>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                By registering, you agree to receive event updates and communications from WeaveIn.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
